// Merlin Visibility Pane — every Merlin conversation across every stream.
//
// Mirrors the backend contracts:
//   * wire shapes  → app/schemas/agent_console_monitor.py
//   * query models → services/agent_console/monitor/models.py
//
// REST calls ride the shared axios `api` instance (baseURL '/api', Bearer
// auto-attached). Admin-only server-side; the page is also adminOnly in the nav.

import api from './api';
import type { ConversationState } from './agentConsole';
import type { ChainDetail } from '../types/portal';

// ---------------------------------------------------------------------------
// Enums (mirror the Python enums — serialized as lowercase strings)
// ---------------------------------------------------------------------------

export type StreamKind =
  | 'console'
  | 'quality_copilot'
  | 'tech_support'
  | 'portal_analyst'
  | 'widget'
  | 'door'
  | 'headless'
  | 'pipeline'
  | 'data_request'
  | 'retired'
  | 'legacy'
  | 'unknown';

export type ChannelKind = 'email' | 'sms' | 'slack' | 'widget';

export type FeedbackFilter = 'any' | 'up' | 'down' | 'none';

export type MonitorSortKey =
  | 'last_activity'
  | 'created'
  | 'title'
  | 'state'
  | 'model'
  | 'profile'
  | 'owner'
  | 'messages'
  | 'tool_calls'
  | 'cost'
  | 'tokens'
  | 'feedback_up'
  | 'feedback_down';

export type SortDirection = 'asc' | 'desc';

export const MONITOR_STATES: ConversationState[] = [
  'created',
  'running',
  'awaiting_input',
  'failed',
  'cancelled',
  'archived',
];

export const MONITOR_CHANNELS: ChannelKind[] = ['email', 'sms', 'slack', 'widget'];

// ---------------------------------------------------------------------------
// Read models
// ---------------------------------------------------------------------------

export interface ChannelBindingSummary {
  id: string;
  channel: ChannelKind;
  counterpart_address: string;
  counterpart_label: string | null;
  anchor_kind: string;
  anchor_id: string;
  external_thread_id: string | null;
}

export interface MonitorRow {
  id: string;
  title: string | null;
  state: ConversationState;
  model: string;
  profile_id: string | null;
  profile_name: string | null;
  stream: StreamKind;
  stream_label: string;
  error_message: string | null;
  created_at: string;
  last_activity_at: string;
  archived_at: string | null;
  user_id: string;
  owner_name: string | null;
  owner_email: string | null;
  email_thread_id: string | null;
  first_user_message: string | null;
  last_message_at: string | null;
  message_count: number;
  assistant_turns: number;
  tool_call_count: number;
  tool_errors: number;
  // Decimal on the wire → string.
  cost_usd: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  feedback_up: number;
  feedback_down: number;
  channels: ChannelBindingSummary[];
}

export interface MonitorPage {
  items: MonitorRow[];
  total: number;
  limit: number;
  offset: number;
  sort_by: MonitorSortKey;
  sort_dir: SortDirection;
}

export interface MonitorTotals {
  conversations: number;
  running: number;
  failed: number;
  archived: number;
  distinct_owners: number;
  messages: number;
  assistant_turns: number;
  tool_calls: number;
  cost_usd: string;
  input_tokens: number;
  output_tokens: number;
  feedback_up: number;
  feedback_down: number;
  with_channels: number;
}

export interface FacetCount {
  value: string;
  label: string;
  count: number;
}

export interface OwnerFacetCount {
  user_id: string;
  name: string | null;
  email: string | null;
  count: number;
}

export interface MonitorFacets {
  streams: FacetCount[];
  profiles: FacetCount[];
  states: FacetCount[];
  models: FacetCount[];
  owners: OwnerFacetCount[];
  channels: FacetCount[];
}

export interface StreamCatalogEntry {
  kind: StreamKind;
  label: string;
  profile_ids: string[];
}

export interface MonitorSummary {
  totals: MonitorTotals;
  facets: MonitorFacets;
  streams: StreamCatalogEntry[];
}

export interface ChannelMessageRead {
  id: string;
  binding_id: string;
  direction: 'inbound' | 'outbound';
  external_message_id: string | null;
  message_id: string | null;
  counterpart: string | null;
  subject: string | null;
  body_text: string;
  body_html: string | null;
  raw_payload: Record<string, unknown>;
  occurred_at: string;
}

export interface ChannelBindingRead extends ChannelBindingSummary {
  sending_account_id: string | null;
  created_at: string;
  messages: ChannelMessageRead[];
}

export interface ConversationChannelsRead {
  conversation_id: string;
  bindings: ChannelBindingRead[];
}

// ---------------------------------------------------------------------------
// Query params (what the filter bar sends)
// ---------------------------------------------------------------------------

/** The filter set as the backend takes it (repeatable keys as arrays). */
export interface MonitorQueryParams {
  q?: string;
  stream?: StreamKind[];
  profile_id?: string[];
  state?: ConversationState[];
  model?: string[];
  owner_id?: string[];
  channel?: ChannelKind[];
  since?: string;
  until?: string;
  feedback?: FeedbackFilter;
  include_archived?: boolean;
}

export interface MonitorPageParams extends MonitorQueryParams {
  sort_by?: MonitorSortKey;
  sort_dir?: SortDirection;
  limit?: number;
  offset?: number;
}

/**
 * Serialize params to a URLSearchParams — one entry per array element so the
 * backend's repeatable multi-selects get `stream=a&stream=b`. Pure (tested).
 */
export function toSearchParams(params: MonitorPageParams): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) out.append(key, String(v));
    } else {
      out.append(key, String(value));
    }
  }
  return out;
}

const BASE = '/admin/agent-console/monitor';

export async function fetchMonitorPage(params: MonitorPageParams): Promise<MonitorPage> {
  const { data } = await api.get<MonitorPage>(`${BASE}/conversations`, {
    params: toSearchParams(params),
  });
  return data;
}

export async function fetchMonitorSummary(
  params: MonitorQueryParams,
): Promise<MonitorSummary> {
  const { data } = await api.get<MonitorSummary>(`${BASE}/summary`, {
    params: toSearchParams(params),
  });
  return data;
}

export async function fetchConversationChannels(
  conversationId: string,
): Promise<ConversationChannelsRead> {
  const { data } = await api.get<ConversationChannelsRead>(
    `${BASE}/conversations/${conversationId}/channels`,
  );
  return data;
}

/**
 * The Gmail thread a conversation is bound to (Tech Support Co-Pilot threads
 * carry `email_thread_id`): the inbox chain, newest first, sent replies
 * included. Empty (`email_count: 0`) for conversations without a thread.
 */
export async function fetchConversationEmailThread(
  conversationId: string,
): Promise<ChainDetail> {
  const { data } = await api.get<ChainDetail>(
    `${BASE}/conversations/${conversationId}/email-thread`,
  );
  return data;
}

/** Bytes of one attachment on a conversation's bound Gmail thread. */
export async function downloadEmailThreadAttachment(
  conversationId: string,
  emailId: string,
  attachmentRef: string,
): Promise<Blob> {
  const { data } = await api.get(
    `${BASE}/conversations/${conversationId}/email-thread/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(attachmentRef)}`,
    { responseType: 'blob' },
  );
  return data as Blob;
}
